import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import fetchDesignEditorPreviewSettings, { DesignEditorPreviewSettings } from 'src/api/designEditorPreviewSettingsService';
import sendPostMessage from 'src/store/app/operation/sendPostMessage';
import { MESSAGE_TYPE } from 'src/store/app/types';
import sendEditorRenderedPreviewsPostMessageOperation
  from 'src/store/editor/operation/sendEditorRenderedPreviewsPostMessageOperation';
import { Store } from 'src/store/index';

// The editor's own defaults: previews come from the server render.
const SERVER_RENDERED_PREVIEWS = {
  includeBackground: true,
  includeForeground: false,
  firstSpreadOnly: false,
  noFlatPreviews: true,
  clientSideRendering: false,
};

// Settings are per product and stock flag, so each pair is fetched once per editor session
// instead of delaying every Preview click. A failed fetch is dropped so the next click retries.
const settingsCache = new Map<string, Promise<DesignEditorPreviewSettings | null>>();

export const clearPreviewSettingsCache = () => settingsCache.clear();

const getPreviewSettings = (productUid: string, hasShutterstockAssets: boolean) => {
  const key = `${productUid}|${hasShutterstockAssets}`;
  let settings = settingsCache.get(key);
  if (!settings) {
    settings = fetchDesignEditorPreviewSettings(productUid, hasShutterstockAssets);
    settingsCache.set(key, settings);
    settings.catch(() => settingsCache.delete(key));
  }
  return settings;
};

// The settings depend on whether stock imagery is used; Shutterstock assets carry the
// provider name in their source, so a text match over the structure is enough.
const hasShutterstockAssets = (designData: any): boolean => (
  JSON.stringify(designData || {}).toLowerCase().includes('shutterstock')
);

const sendPreviewSettings = (previews: typeof SERVER_RENDERED_PREVIEWS) => sendPostMessage({
  type: MESSAGE_TYPE['editor.setEditorHostSettings'],
  // The editor replaces `previews` as a whole, so every field is sent.
  data: { previews },
});

/**
 * Answers the editor's preview request the way the Create editor does: when the product
 * supports it the editor renders the preview in the browser (fast, zoomable at full
 * resolution) and no server render is requested. Otherwise, or when the settings cannot
 * be fetched, the editor is put back on server renders, which may have been switched off
 * for a previously selected product.
 */
const loadEditorPreviewsOperation = (designData: any) => async (
  dispatch: ThunkDispatch<Store, undefined, AnyAction>,
) => {
  const productUid = designData?.product_uid;
  let settings: DesignEditorPreviewSettings | null = null;
  if (productUid) {
    try {
      settings = await getPreviewSettings(productUid, hasShutterstockAssets(designData));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Cannot fetch editor preview settings, falling back to server renders: ', e);
    }
  }

  if (settings?.clientSideRendering) {
    sendPreviewSettings({
      includeBackground: settings.includeBackground,
      includeForeground: settings.includeForeground,
      firstSpreadOnly: settings.firstSpreadOnly,
      noFlatPreviews: settings.noFlatPreviews,
      clientSideRendering: true,
    });
    return;
  }

  sendPreviewSettings(settings ? {
    includeBackground: settings.includeBackground,
    includeForeground: settings.includeForeground,
    firstSpreadOnly: settings.firstSpreadOnly,
    noFlatPreviews: true,
    clientSideRendering: false,
  } : SERVER_RENDERED_PREVIEWS);
  await dispatch(sendEditorRenderedPreviewsPostMessageOperation(designData));
};

export default loadEditorPreviewsOperation;
